import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to fetch route and vehicle data from an external cacher (e.g. 511 realtime cacher).
 *
 * Config shape (from campaign-config.json → externalRoutes):
 * {
 *   "baseUrl": "https://st511datacherdev.blob.core.windows.net/cache-container",
 *   "pollInterval": 30000,
 *   "routes": [
 *     { "id": "122", "staticPath": "samtrans/static/122.json", "realtimePath": "samtrans/122.json", "color": "#0072CE" },
 *     ...
 *   ]
 * }
 */
const useExternalRoutes = (config) => {
  const [externalRoutes, setExternalRoutes] = useState([]);
  const [externalVehicles, setExternalVehicles] = useState([]);
  const intervalRef = useRef(null);
  // Cache: routeId → { "0": "Daly City BART", "1": "Palo Alto Transit Center" }
  const headsignCacheRef = useRef(new Map());

  const fetchStaticData = useCallback(async () => {
    if (!config || !config.baseUrl || !Array.isArray(config.routes)) return [];

    const routes = [];
    for (const routeCfg of config.routes) {
      if (!routeCfg.staticPath) continue;
      try {
        const url = `${config.baseUrl}/${routeCfg.staticPath}`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const envelope = await resp.json();
        const d = envelope.data;
        if (!d) continue;

        // Cache headsigns for this route
        if (d.headsigns) {
          headsignCacheRef.current.set(routeCfg.id, d.headsigns);
        }

        routes.push({
          id: routeCfg.id,
          name: d.routeLongName || `Route ${routeCfg.id}`,
          trip_id: `external-${routeCfg.id}`,
          coordinates: d.shape && d.shape.coordinates
            ? d.shape.coordinates.map(coord => [coord[1], coord[0]])
            : [],
          stops: (d.stops || []).map(s => ({
            stop_id: s.stopId,
            stop_name: s.stopName,
            stop_lat: s.latitude,
            stop_lon: s.longitude,
            stop_sequence: s.stopSequence
          })),
          color: routeCfg.color || '#666666',
          isFallback: true,
          isFleet: true,
          isExternal: true
        });
      } catch (err) {
        console.warn(`Failed to fetch external static route ${routeCfg.id}:`, err);
      }
    }
    return routes;
  }, [config]);

  const fetchRealtimeData = useCallback(async () => {
    if (!config || !config.baseUrl || !Array.isArray(config.routes)) return [];

    const vehicles = [];
    for (const routeCfg of config.routes) {
      if (!routeCfg.realtimePath) continue;
      try {
        const url = `${config.baseUrl}/${routeCfg.realtimePath}`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const envelope = await resp.json();
        const d = envelope.data;
        if (!d || !Array.isArray(d.vehicles)) continue;

        const headsigns = headsignCacheRef.current.get(routeCfg.id) || {};

        for (const v of d.vehicles) {
          const dirId = v.directionId != null ? String(v.directionId) : null;
          const headsign = dirId != null ? (headsigns[dirId] || null) : null;

          vehicles.push({
            id: `ext-${v.vehicleId}`,
            routeId: routeCfg.id,
            icon: routeCfg.icon || 'bus.svg',
            arrowColor: routeCfg.color || '#666666',
            currentPosition: [v.latitude, v.longitude],
            heading: v.bearing || 0,
            route: routeCfg.id,
            tripId: v.tripId,
            tripData: headsign ? { trip_headsign: headsign } : null,
            isExternal: true
          });
        }
      } catch (err) {
        console.warn(`Failed to fetch external realtime route ${routeCfg.id}:`, err);
      }
    }
    return vehicles;
  }, [config]);

  useEffect(() => {
    if (!config || !config.baseUrl || !Array.isArray(config.routes) || config.routes.length === 0) {
      setExternalRoutes([]);
      setExternalVehicles([]);
      return;
    }

    // Fetch static once, then poll realtime
    const init = async () => {
      const routes = await fetchStaticData();
      setExternalRoutes(routes);
      const vehicles = await fetchRealtimeData();
      setExternalVehicles(vehicles);
    };
    init();

    const interval = config.pollInterval || 30000;
    intervalRef.current = setInterval(async () => {
      const vehicles = await fetchRealtimeData();
      setExternalVehicles(vehicles);
    }, interval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [config, fetchStaticData, fetchRealtimeData]);

  return { externalRoutes, externalVehicles };
};

export default useExternalRoutes;
